/* eslint-disable react/no-unknown-property */
import React, { useState, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import {
  OrbitControls, Text, Grid, Edges,
} from '@react-three/drei';
import { useQuery } from '@apollo/client';
import GET_LOAD_PLAN_V2_FOR_3D from '@graphql/TH_getLoadPlanV2For3D';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Convert centimetres to Three.js world-units (1 unit = 1 metre) */
const CM = 0.01;

/** Horizontal gap between adjacent containers in the scene (metres) */
const CONTAINER_GAP = 1.5;

/** Deterministic colour palette for the scan-ID boxes */
const PALETTE = [
  '#2196f3', '#4caf50', '#ff5722', '#ffc107', '#9c27b0',
  '#00bcd4', '#ff9800', '#e91e63', '#009688', '#3f51b5',
  '#f44336', '#8bc34a', '#ff4081', '#00e5ff', '#d500f9',
];

// ─── ScanIdBox ────────────────────────────────────────────────────────────────
/**
 * Renders one overpack / scan-ID as a coloured box inside the container.
 *
 * Coordinate mapping:
 *   Backend stores placement as the bottom-front-left corner (door-side = Z 0).
 *   Three.js mesh origin = box centre, and its +Z points toward the viewer,
 *   so we negate Z when converting.
 *
 * Rotation:
 *   RotationOrientation = 0 → L×W footprint (LengthCm along Z, WidthCm along X)
 *   RotationOrientation ≠ 0 → W×L footprint (swap L and W on X/Z)
 */
function ScanIdBox({
  attachment, colorIndex, isSelected, onSelect,
}) {
  const [hovered, setHovered] = useState(false);

  const scanId = attachment.ScanId ?? {};
  const rotated = (attachment.RotationOrientation ?? 0) !== 0;

  // Visual dimensions after footprint rotation
  const rawW = scanId.WidthCm ?? 50;
  const rawL = scanId.LengthCm ?? 50;
  const rawH = scanId.HeightCm ?? 50;
  const visW = rotated ? rawL : rawW; // along Three.js X
  const visD = rotated ? rawW : rawL; // along Three.js Z (depth)
  const visH = rawH; // along Three.js Y

  const w = visW * CM;
  const h = visH * CM;
  const d = visD * CM;

  // Shift from corner to centre; negate Z so the door faces the viewer
  const px = (attachment.StackPosition_X_Cm + visW / 2) * CM;
  const py = (attachment.StackPosition_Y_Cm + visH / 2) * CM;
  const pz = -((attachment.StackPosition_Z_Cm + visD / 2) * CM);

  const color = PALETTE[colorIndex % PALETTE.length];
  const active = hovered || isSelected;

  const handleClick = useCallback(
    (e) => { e.stopPropagation(); onSelect(isSelected ? null : attachment); },
    [isSelected, attachment, onSelect],
  );

  return (
    <group position={[px, py, pz]}>
      <mesh
        castShadow
        onClick={handleClick}
        onPointerEnter={(e) => { e.stopPropagation(); setHovered(true); }}
        onPointerLeave={() => setHovered(false)}
      >
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={active ? 0.92 : 0.68}
          emissive={new THREE.Color(color)}
          emissiveIntensity={active ? 0.45 : 0.06}
          roughness={0.45}
          metalness={0.05}
        />
        <Edges
          color={active ? '#ffffff' : 'rgba(0,0,0,0.45)'}
          lineWidth={active ? 2.5 : 1}
        />
      </mesh>

      {/* Show the scan-ID label when hovered or selected */}
      {active && (
        <Text
          position={[0, h / 2 + 0.04, 0]}
          fontSize={0.055}
          color="white"
          anchorX="center"
          anchorY="bottom"
          outlineWidth={0.005}
          outlineColor="#000"
          renderOrder={2}
        >
          {scanId.ScanId ?? `#${attachment.ScanId_PoPartLocation_Fk}`}
        </Text>
      )}
    </group>
  );
}

// ─── ContainerShell ───────────────────────────────────────────────────────────
/**
 * Renders the outer shell of a shipping container as a semi-transparent
 * blue wireframe box.  Origin is at the bottom-front-left corner so that
 * scan-ID placement coordinates (also measured from that corner) line up
 * without any additional offset.
 */
function ContainerShell({ container }) {
  const w = (container.ContainerDimensionWidth_Cm ?? 235) * CM;
  const h = (container.ContainerDimensionHeight_Cm ?? 239) * CM;
  const d = (container.ContainerDimensionLength_Cm ?? 589) * CM;

  const utilPct = (container.CurrentUtilizationPercent ?? 0).toFixed(1);
  const label = [
    container.ContainerTypeDetails?.Description ?? '',
    container.ContainerNumber ? `· ${container.ContainerNumber}` : '',
    `· ${utilPct}% full`,
  ].filter(Boolean).join(' ');

  // Shift mesh centre to (w/2, h/2, -d/2) so origin stays at bottom-front-left
  return (
    <group position={[w / 2, h / 2, -d / 2]}>
      <mesh>
        <boxGeometry args={[w, h, d]} />
        {/* Back-face only so the interior is visible */}
        <meshStandardMaterial
          color="#b3d4f5"
          transparent
          opacity={0.04}
          side={THREE.BackSide}
        />
        <Edges color="#4a90d9" lineWidth={1.5} />
      </mesh>

      {/* Floating label above the container */}
      <Text
        position={[0, h / 2 + 0.14, 0]}
        fontSize={0.12}
        color="#90caf9"
        anchorX="center"
        anchorY="bottom"
        outlineWidth={0.007}
        outlineColor="#050510"
        maxWidth={w * 3}
      >
        {label}
      </Text>

      {/* Utilisation bar along the container floor */}
      <UtilisationBar
        containerW={w}
        containerD={d}
        floorY={-h / 2 + 0.004}
        utilisation={(container.CurrentUtilizationPercent ?? 0) / 100}
      />
    </group>
  );
}

/** A thin coloured strip on the floor indicating how full the container is */
function UtilisationBar({
  containerW, containerD, floorY, utilisation,
}) {
  if (utilisation <= 0) return null;
  const barD = containerD * utilisation;
  // eslint-disable-next-line no-nested-ternary
  const color = utilisation > 0.85 ? '#ff5252' : utilisation > 0.60 ? '#ffc107' : '#4caf50';
  // Bar starts from Z=0 (door side); in world space that is +containerD/2, so offset back
  const posZ = containerD / 2 - barD / 2;

  return (
    <mesh position={[0, floorY, posZ]}>
      <boxGeometry args={[containerW * 0.98, 0.008, barD]} />
      <meshStandardMaterial color={color} transparent opacity={0.65} />
    </mesh>
  );
}

// ─── Stats bar ────────────────────────────────────────────────────────────────
function StatsBar({ containers }) {
  const totals = useMemo(() => containers.reduce(
    (acc, c) => ({
      weight: acc.weight + (c.CurrentTotalWeight_Kg ?? 0),
      volume: acc.volume + (c.TotalVolume_CBM ?? 0),
      items: acc.items + (c.AttachedScanIds?.length ?? 0),
    }),
    { weight: 0, volume: 0, items: 0 },
  ), [containers]);

  const chips = [
    { label: 'Containers', value: containers.length },
    { label: 'Items', value: totals.items },
    { label: 'Weight', value: `${totals.weight.toFixed(0)} kg` },
    { label: 'Volume', value: `${totals.volume.toFixed(2)} CBM` },
  ];

  return (
    <div style={{
      position: 'absolute',
      top: 56,
      left: 16,
      zIndex: 10,
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
    }}
    >
      {chips.map(({ label, value }) => (
        <div
          key={label}
          style={{
            background: 'rgba(10,10,30,0.78)',
            backdropFilter: 'blur(6px)',
            border: '1px solid #2a3a5a',
            borderRadius: 6,
            padding: '5px 12px',
            color: '#e0e0e0',
            fontFamily: 'sans-serif',
            fontSize: 12,
          }}
        >
          <span style={{ color: '#90caf9', marginRight: 6 }}>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

// ─── Selection info panel ─────────────────────────────────────────────────────
function SelectionPanel({ attachment, onClose }) {
  if (!attachment) return null;
  const s = attachment.ScanId ?? {};

  const rows = [
    ['Scan ID', s.ScanId ?? '—'],
    ['Stack Order', `#${attachment.StackingOrder ?? '—'}`],
    ['Rotation', attachment.RotationOrientation ? 'Rotated (W×L)' : 'Normal (L×W)'],
    ['Pos X', `${attachment.StackPosition_X_Cm} cm`],
    ['Pos Y', `${attachment.StackPosition_Y_Cm} cm`],
    ['Pos Z', `${attachment.StackPosition_Z_Cm} cm`],
    ['L × W × H', `${s.LengthCm} × ${s.WidthCm} × ${s.HeightCm} cm`],
    ['Weight', `${s.WeightKg ?? '—'} kg`],
    ['Volume', `${s.VolumeCbm ?? '—'} CBM`],
    ['Packed By', s.PackedBy ?? '—'],
    ['Packed Date', s.PackedDate ?? '—'],
  ];

  return (
    <div style={{
      position: 'absolute',
      bottom: 24,
      right: 24,
      zIndex: 20,
      background: 'rgba(10,10,30,0.90)',
      backdropFilter: 'blur(10px)',
      color: '#e0e0e0',
      borderRadius: 10,
      padding: '14px 18px',
      minWidth: 240,
      fontFamily: 'monospace',
      fontSize: 12,
      boxShadow: '0 4px 28px rgba(0,0,0,0.65)',
      border: '1px solid #4a90d9',
    }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
      }}
      >
        <span style={{ fontWeight: 'bold', color: '#4a90d9', fontSize: 14 }}>
          {s.ScanId ?? 'Scan ID Details'}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#aaa',
            cursor: 'pointer',
            fontSize: 16,
            padding: 0,
          }}
        >
          ✕
        </button>
      </div>

      {rows.map(([label, val]) => (
        <div
          key={label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 4,
          }}
        >
          <span style={{ color: '#90caf9', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ textAlign: 'right', color: '#ddd' }}>{val}</span>
        </div>
      ))}

      {attachment.PlacementNotes && (
        <div style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid #2a3a5a',
          color: '#aaa',
          fontStyle: 'italic',
        }}
        >
          📝
          {' '}
          {attachment.PlacementNotes}
        </div>
      )}
    </div>
  );
}

// ─── State helpers ────────────────────────────────────────────────────────────
function LoadingScreen() { return <FullScreen color="#90caf9">Loading load plan…</FullScreen>; }
function ErrorScreen({ msg }) { return <FullScreen color="#ff5252">{`Error: ${msg}`}</FullScreen>; }
function EmptyScreen({ id }) { return <FullScreen color="#aaa">{`No load plan data found for Warehouse Out #${id}.`}</FullScreen>; }
function FullScreen({ color, children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      background: '#0d0d1a',
      color,
      fontFamily: 'sans-serif',
      fontSize: 16,
    }}
    >
      {children}
    </div>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────
/**
 * LoadPlanViewer3D
 *
 * @param {number}  warehouseOutId  - The WarehouseOut.Id to display
 * @param {string}  height          - CSS height of the viewer (default '60vh')
 *
 * Usage:
 *   <LoadPlanViewer3D warehouseOutId={42} />
 *   <LoadPlanViewer3D warehouseOutId={42} height="600px" />
 */
export default function LoadPlanViewer3D({ warehouseOutId, height = '60vh' }) {
  const [selected, setSelected] = useState(null);

  const { loading, error, data } = useQuery(GET_LOAD_PLAN_V2_FOR_3D, {
    variables: { warehouseOutId },
    skip: !warehouseOutId,
    fetchPolicy: 'cache-and-network',
  });

  const { containers, woDetails } = useMemo(() => {
    try {
      // The Apollo dataLink in client.js unwraps the first query key, so
      // `data` is already `{ code, success, message, results }`.
      // `results` is a JSON scalar field — guard against it arriving as a string.
      const raw = data?.results ?? {};
      const res = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return {
        containers: res.LoadPlanDetails ?? [],
        woDetails: res.warehouseOutDetails ?? null,
      };
    } catch {
      return { containers: [], woDetails: null };
    }
  }, [data]);

  // Camera target centres on all containers combined
  const cameraTarget = useMemo(() => {
    if (!containers.length) return [0, 1.2, -3];
    const lastW = (containers[containers.length - 1]?.ContainerDimensionWidth_Cm ?? 235) * CM;
    const totalW = (containers.length - 1) * (lastW + CONTAINER_GAP) + lastW;
    return [totalW / 2, 1.2, -3];
  }, [containers]);

  if (loading && !data) return <div style={{ height }}><LoadingScreen /></div>;
  if (error) return <div style={{ height }}><ErrorScreen msg={error.message} /></div>;
  if (!containers.length) return <div style={{ height }}><EmptyScreen id={warehouseOutId} /></div>;

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height,
      background: '#0d0d1a',
      overflow: 'hidden',
    }}
    >

      {/* ── Header bar ───────────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 48,
        zIndex: 10,
        background: 'rgba(10,10,30,0.82)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #1e2a4a',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 20px',
        color: '#e0e0e0',
        fontFamily: 'sans-serif',
        fontSize: 14,
      }}
      >
        <span style={{ fontWeight: 700, color: '#90caf9', fontSize: 15 }}>
          📦 Load Plan 3D Viewer
        </span>
        {woDetails && (
          <span style={{ color: '#aaa' }}>
            {`WO# ${woDetails.JobNumber}${woDetails.JobStatus ? ` · ${woDetails.JobStatus}` : ''}`}
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: '#555', fontSize: 11 }}>
          Scroll = zoom · Left-drag = rotate · Right-drag = pan · Click box = inspect
        </span>
      </div>

      {/* ── Stats chips ──────────────────────────────────────────────────────── */}
      <StatsBar containers={containers} />

      {/* ── Three.js canvas ──────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 48, left: 0, right: 0, bottom: 0,
      }}
      >
        <Canvas
          camera={{
            position: [4, 3.5, 9],
            fov: 48,
            near: 0.01,
            far: 600,
          }}
          shadows
          onPointerMissed={() => setSelected(null)}
        >
          {/* Lighting */}
          <ambientLight intensity={0.55} />
          <directionalLight
            position={[12, 14, 8]}
            intensity={1.0}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
          />
          <directionalLight position={[-8, 6, -6]} intensity={0.35} />

          {/* Ground grid */}
          <Grid
            args={[60, 60]}
            position={[0, -0.005, 0]}
            cellSize={0.5}
            cellThickness={0.4}
            cellColor="#1c1c36"
            sectionSize={2.35}
            sectionThickness={0.8}
            sectionColor="#2a2a5a"
            fadeDistance={35}
            infiniteGrid
          />

          {/* One group per container, spaced out along the X axis */}
          {containers.map((container, ci) => {
            const containerW = (container.ContainerDimensionWidth_Cm ?? 235) * CM;
            const offsetX = ci * (containerW + CONTAINER_GAP);

            return (
              <group key={container.Id} position={[offsetX, 0, 0]}>
                <ContainerShell container={container} />

                {(container.AttachedScanIds ?? []).map((attachment, ai) => (
                  <ScanIdBox
                    key={attachment.Id}
                    attachment={attachment}
                    colorIndex={ai}
                    isSelected={selected?.Id === attachment.Id}
                    onSelect={setSelected}
                  />
                ))}
              </group>
            );
          })}

          <OrbitControls
            makeDefault
            enableDamping
            dampingFactor={0.06}
            minDistance={0.5}
            maxDistance={120}
            target={cameraTarget}
          />
        </Canvas>
      </div>

      {/* ── Selection panel (bottom-right) ───────────────────────────────────── */}
      <SelectionPanel
        attachment={selected}
        onClose={() => setSelected(null)}
      />

      {/* ── Controls hint (bottom-left) ──────────────────────────────────────── */}
      <div style={{
        position: 'absolute',
        bottom: 24,
        left: 24,
        zIndex: 20,
        background: 'rgba(10,10,30,0.70)',
        backdropFilter: 'blur(6px)',
        color: '#666',
        borderRadius: 8,
        padding: '8px 12px',
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: 1.7,
        border: '1px solid #1e2a4a',
      }}
      >
        <div>🖱 Left-drag · rotate</div>
        <div>🖱 Scroll · zoom</div>
        <div>🖱 Right-drag · pan</div>
        <div>📦 Click box · inspect</div>
      </div>
    </div>
  );
}
